import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import d3, { useD3 } from 'hooks/useD3';
import { randomValueBetween } from 'helpers/number';
import './index.css';

const COLORS = (index, day = false) => {
  // Day flag just in case we want to go with color later
  const colors = day
    ? [
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
        '#ffffff',
      ]
    : [
        '#66ff00',
        '#ff08e8',
        '#0ff0fc',
        '#ffcf09',
        '#01f9c6',
        '#be03fd',
        '#21fc0d',
        '#ff0490',
        '#ccff02',
        '#fc74fd',
      ];

  return colors[index];
};

const RadialBarChart = ({
  data = [],
  onItemClick = () => null,
  onItemMouseOver = () => null,
  onItemMouseOut = () => null,
  day = false,
  valueKey = 'totalPremium',
}) => {
  const [showAnimation, setShowAnimation] = React.useState(true);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const timerHandle = React.useRef(null);

  useEffect(() => {
    const autoPlay = (delay = 5000) => {
      if (timerHandle) {
        timerHandle.current = setTimeout(() => {
          const select = randomValueBetween(0, data.length - 1);
          setShowAnimation(false);
          setSelectedItem(data[select][valueKey]);
          onItemClick(data[select], COLORS(select, day));
          autoPlay();
        }, delay);
      }
    };
    setShowAnimation(true);
    setSelectedItem(null);
    clearTimeout(timerHandle.current);
    onItemClick({ name: '' }, '');
    autoPlay(3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const ref = useD3(
    (svg) => {
      const parent = document.getElementById('TopInsurers');
      const width = parent?.offsetWidth || 700,
        height = 425,
        chartRadius = height / 2 - 40;

      svg.selectAll('* > g').attr('class', 'old-render');
      let stage = svg
        .append('g')
        .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

      const PI = Math.PI,
        arcMinRadius = 12,
        arcPadding = 9,
        labelPadding = -5,
        numTicks = 0;

      let scale = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => parseInt(d[valueKey])) * 1.5])
        .range([0, 2 * PI]);

      let ticks = scale.ticks(numTicks).slice(0, -1);
      let keys = data.map((d, i) => d.name);
      //number of arcs
      const numArcs = keys.length;

      const arcWidth = (chartRadius - arcMinRadius - numArcs * arcPadding) / numArcs;

      const rad2deg = (angle) => {
        return (angle * 180) / PI;
      };

      const getInnerRadius = (index) => {
        return arcMinRadius + (numArcs - (index + 1)) * (arcWidth + arcPadding);
      };

      const getOuterRadius = (index) => {
        return getInnerRadius(index) + arcWidth;
      };

      let arc = d3
        .arc()
        .innerRadius((d, i) => getInnerRadius(i))
        .outerRadius((d, i) => getOuterRadius(i))
        .startAngle(0)
        .cornerRadius(5)
        .endAngle((d, i) => scale(d));

      stage
        .append('path')
        .attr('class', 'state-outline')
        .attr('width', 200)
        .style('transform', 'scale(0.25) translate(-500px, -680px)')
        .style('fill', () => (day ? '#fff' : '#66ff00'))
        .style('filter', () => `drop-shadow(0px 0px 10px ${day ? '#fff' : '#66ff00'}`)
        .attr(
          'd',
          `M387.7,22.3c-2.8,0-6.6,0.1-11.2,0.3c-9.3,0.4-22,1.1-37,2.1
        c-30,2-69,4.9-106.8,8.3c-37.2,3.3-57.2,5.2-69.4,7.1c-6.1,1-10.4,2-14.2,3.7c-3.8,1.7-6.9,4.2-8.9,6.3c-1.8,1.9-2.5,3.2-3.7,5.1
        c-1.2,1.9-2.5,4.2-3.9,6.7c-2.9,5.2-6.1,11.5-9.3,18c-9.1,19-11.7,23.1-20.6,32.9l-14.3,15.6l1.5,12.6c0.5,3.9,0.2,3.9,0.5,3.1
        c0.3-0.8-0.5,0.7-4,4.3l-9.8,10.1l-1.2,24.4c-0.5,11.5-1.5,17.5-2.2,19.2c-0.7,1.7-0.6,1.6-5.6,4c-3.2,1.5-6.4,3.3-9.2,6.2
        c-2.8,2.9-4.8,7.1-5.6,11.2c-1.4,8.2,0.3,16.3,3,29.5l3.1,14.7l-3.3,13.4c-4.2,17-5.5,32.3-2.8,50.1c2.7,17.8,9.1,38.1,20,67
        c3.2,8.5,6.1,16.7,8.1,22.9c1,3.1,1.9,5.8,2.4,7.7c0.1,0.4,0.2,0.8,0.2,1.2c-3.6,2.9-7,5.5-8.8,10.8c-1.9,5.5-0.5,10.2,0.8,14.4
        c0.4,1.3,0.4,1.6,0.5,2.2c0-0.3-1.6,1.3-7.7,4l-16.7,7.6l-1.3,13.8c-0.3,3.1-0.5,3.7-0.7,4.1c-0.2,0.4-1.2,1.7-4.1,4.4
        c-3.5,3.3-6.5,6.4-8.6,10.7c-2.1,4.3-3,8.6-4.3,15c-1.6,7.7-2.6,12.6-1.9,18.2c0.3,2.3,1.3,5,2.9,7.3c-0.2,0.2-0.2,0.2-0.4,0.4
        c-6.5,6.3-10.7,12.8-12.9,20.5c-3.3,11.2-4.2,24.8-1.2,35.7l3.1,11.2l109,0.9c46.2,0.4,71.7,0.6,85.9,1.1c7.1,0.2,11.3,0.5,13.5,0.8
        c2.2,0.3,1.6,0.3,1,0c1.3,0.8,1.1,0.9,0.9,0.6c-0.2-0.4,0-0.5-0.2,1.2c-0.4,3.5-3.2,12.4-9,27.5l-5,13l8.8,10
        c0.5,0.6,5.7,9.2,8.2,15.2c8.8,21.6,12.9,31.5,22.4,37.3c1.6,1,3.1,1.8,4.6,2.5c0.7,0.4,1.4,0.7,2.2,1c0.8,0.3,1.1,0.9,4.3,0.9
        c3.5,0,3.4-0.5,4-0.8c0.6-0.2,1.1-0.4,1.6-0.7c1-0.5,2-1,3.2-1.6c2.3-1.3,5.2-2.9,8.2-4.7c4.8-2.9,15.6-8.1,22.1-10.5
        c8.8-3.3,17.1-7.6,23-12.7c4.2-3.7,6.8-5.5,8.3-6.2s2-0.8,4.4-0.8c4.7,0,6.5,0.7,17.9,7.7c6.2,3.8,12,6.5,18,7.5c6,1,13,0,18-4.5
        c1.8-1.6,5.5-4.8,7.4-9.7c1.9-4.9,2.5-10.3,3-19.5c1-18.3,0.8-52.4,0.7-122.5c-0.2-92.6-1-215.6-1.8-316.7
        c-0.4-50.6-0.9-95.6-1.3-128.7c-0.2-16.5-0.4-30.1-0.6-39.8c-0.1-4.9-0.2-8.8-0.3-11.7c-0.1-2.9-0.1-4.1-0.3-6.2v0
        c-0.3-2.7-0.8-5.1-1.3-7.3c-0.3-1.1-0.6-2.1-1-3.2c-0.2-0.6-0.5-1.2-1-2c-0.5-0.8-0.9-2.1-3.6-3.8c-2.8-1.7-3.4-1.4-4.1-1.5
        c-0.7-0.1-1.3-0.2-1.8-0.2C390.3,22.3,389.2,22.3,387.7,22.3L387.7,22.3z M384.6,42.2c0,0.3,0.1,0.4,0.1,0.7
        c-0.1-0.9,0.1,1.5,0.2,4.3c0.1,2.8,0.2,6.6,0.3,11.5c0.2,9.7,0.4,23.2,0.6,39.7c0.4,33,0.9,78.1,1.3,128.6
        c0.9,101.1,1.6,224,1.8,316.6c0.2,70.1,0.3,104.5-0.7,121.4c-0.5,8.4-1.3,12.3-1.7,13.3c-0.4,0.9,0.2,0.1-2.1,2.2
        c0.3-0.3,0.7,0.1-1.5-0.3c-2.2-0.4-6.1-1.8-10.8-4.8c-12.1-7.6-19.9-10.8-28.4-10.8c-4.3,0-9,0.8-13.2,2.9c-4.3,2.1-7.9,5-12.5,9.1
        c-2.1,1.9-9.9,6.4-16.9,9.1c-8.1,3-18.6,8-25.4,12.1c-2.5,1.5-4.9,2.8-6.7,3.9c-0.3-0.2-0.5-0.3-0.8-0.4c-0.5-0.3-5.8-6.9-14.4-27.9
        c-3.2-7.8-6.8-15.3-11.7-20.8l-0.8-1l0.7-1.8c5.9-15.4,9.3-24.4,10.2-32.6c0.4-4.1,0.1-8.5-1.9-12.3c-2-3.8-5-6.4-7.7-8.1v0
        c-2.5-1.6-5.7-2.5-9.2-3c-3.6-0.5-8.2-0.8-15.5-1c-14.7-0.5-40.1-0.7-86.4-1.1l-93.3-0.7c-0.5-5.4-0.4-16.1,1.2-21.6v0
        c1.5-5.2,2.5-6.8,7.7-11.8c4.4-4.3,7.1-5.8,9.5-11.6c0.6-1.5,1.2-3.5,0.9-6.1c-0.3-2.5-1.8-5.1-3.3-6.6c-1.3-1.3-2.4-1.9-3.4-2.4
        c0.1-1.5,0.2-3.8,1.4-9.5c1.3-6.3,2.1-9.1,2.7-10.3c0.6-1.2,1.2-2,4.4-5c3.3-3.1,6-5.7,8-9.5c2-3.8,2.6-7.7,3-11.6l0.2-2.4l6.3-2.9
        c7.2-3.3,12.5-5.2,16.6-11.2c2.1-3,3-7,2.9-10.2c-0.1-3.2-0.8-5.8-1.6-8.5c-0.4-1.5-0.5-2-0.6-2.4c-0.2,0.6-0.2,0.3,2.6-2
        c4.7-3.8,7.2-8.4,7.2-14.9v0c0-4.3-0.6-4.5-1.3-6.8c-0.7-2.3-1.5-5.2-2.6-8.5c-2.2-6.6-5.1-15-8.4-23.7c-10.7-28.5-16.6-47.7-19-63
        c-2.3-15.4-1.3-27,2.5-42.4l4.4-17.8l-4-19.1c-2.7-12.8-3.3-20.1-2.9-22.1c0.2-1,0.1-0.6,0.3-0.9c0.2-0.2,1.3-1,3.5-2.1
        c6-2.9,12.3-7.2,15.2-14.1c2.9-6.9,3.2-14.2,3.7-26l0.8-16.9l4.6-4.7c3.5-3.7,6.3-6,8.2-10.7c1.9-4.7,1.2-8.8,0.8-12.8l-0.4-3.6
        l8.1-8.9c9.7-10.7,14.5-18.2,23.9-37.7c3-6.2,6.1-12.3,8.7-17c1.3-2.4,2.5-4.4,3.4-5.9c0.9-1.5,1.9-2.6,1.2-1.9
        c1.1-1.2,1.1-1.2,2.4-1.8c1.3-0.6,4.2-1.4,9.6-2.3c10.7-1.7,30.8-3.7,67.9-7c37.6-3.3,76.5-6.3,106.3-8.3c14.9-1,27.5-1.7,36.5-2
        C380.4,42.2,382.4,42.2,384.6,42.2L384.6,42.2z`,
        );

      let radialAxis = stage
        .append('g')
        .attr('class', 'r axis')
        .selectAll('g')
        .data(data)
        .enter()
        .append('g');

      radialAxis
        .append('circle')
        .attr('cx', labelPadding - 3)
        .attr('cy', (d, i) => -getOuterRadius(i) + arcPadding - 5)
        .attr('r', 5)
        .style('fill', (d, i) => COLORS(i, day))
        .style(
          'filter',
          (d, i) => `drop-shadow(0px 0px ${day ? '3px' : '6px'} ${day ? '#fff' : COLORS(i, day)})`,
        );

      let axialAxis = stage
        .append('g')
        .attr('class', 'a axis')
        .selectAll('g')
        .data(ticks)
        .enter()
        .append('g')
        .attr('transform', (d) => 'rotate(' + (rad2deg(scale(d)) - 90) + ')');

      axialAxis.append('line').attr('x2', chartRadius);

      axialAxis
        .append('text')
        .attr('x', chartRadius + 10)
        .style('text-anchor', (d) => (scale(d) >= PI && scale(d) < 2 * PI ? 'end' : null))
        .attr(
          'transform',
          (d) => 'rotate(' + (90 - rad2deg(scale(d))) + ',' + (chartRadius + 10) + ',0)',
        )
        .text((d) => d);

      //data arcs
      let arcs = stage
        .append('g')
        .selectAll('path')
        .data(data)
        .enter()
        .append('path')
        .attr('class', (d, i) => {
          if (selectedItem === null) {
            return `arc`;
          }
          return `data tube-unlit arc ${
            selectedItem === d[valueKey] ? 'arc-selected' : 'arc-unselected'
          }`;
        })
        .attr('fill', (d, i) => COLORS(i, day))
        .style(
          'filter',
          (d, i) => `drop-shadow(0px 0px ${day ? '3px' : '6px'} ${day ? '#fff' : COLORS(i)})`,
        );

      arcs.on('mouseover', function (d) {
        d3.selectAll('.arc').attr('class', 'arc arc-unlit');
        onItemMouseOver(d);
      });

      arcs.on('mouseout', function (d) {
        d3.selectAll('.arc-unlit').attr('class', 'arc');
        onItemMouseOut(d);
      });

      arcs.on('click', function (d, i) {
        setShowAnimation(false);
        if (timerHandle && timerHandle.current) {
          clearTimeout(timerHandle.current);
        } else {
          d3.selectAll('.arc').attr('class', 'arc arc-unselected');
          d3.select(this).attr('class', 'arc arc-selected');
        }

        onItemClick(d, COLORS(i, day));
      });

      const arcTween = (d, i) => {
        let interpolate = d3.interpolate(0, d[valueKey]);
        return (t) => arc(interpolate(t), i);
      };

      arcs
        .transition()
        .delay((d, i) => (showAnimation ? i * 200 : 0))
        .duration(showAnimation ? 1000 : 0)
        .attrTween('d', arcTween);

      svg.selectAll('.old-render').remove();
    },
    [data, day, selectedItem, showAnimation],
  );
  return <svg ref={ref} id="RadialBarChart" height="400" />;
};

RadialBarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default RadialBarChart;
